import React, { useEffect, useRef, useState } from "react";
import { FaSyncAlt } from "react-icons/fa";

interface CacheRefreshButtonProps {
  label: string;
  /** Starts the refresh; resolves once the service accepted it. */
  refresh: () => Promise<unknown>;
  /** Passed to onSuccess, for the parent to show under the sub-tab bar. */
  message: string;
  onSuccess: (message: string) => void;
  onError: (label: string, err: unknown) => void;
}

/**
 * A refresh action at the right end of a sub-tab bar (Refresh entitlements, Refresh
 * assets). Ignores clicks while its call is in flight; the parent shows the outcome.
 * The button unmounts when the top tab changes; a call that settles after that is
 * ignored, so its message never lands under another tab's bar.
 */
const CacheRefreshButton: React.FC<CacheRefreshButtonProps> = ({
  label,
  refresh,
  message,
  onSuccess,
  onError,
}) => {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    let outcome: () => void;
    try {
      await refresh();
      outcome = () => onSuccess(message);
    } catch (err) {
      outcome = () => onError(label, err);
    }
    busyRef.current = false;
    if (!mountedRef.current) return;
    setBusy(false);
    outcome();
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="ml-auto flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <FaSyncAlt className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} aria-hidden="true" />
      {label}
    </button>
  );
};

export default CacheRefreshButton;
